/**
 * Formatting Utilities for Vendor Dashboard
 */

import { format, formatDistanceToNow, parseISO, differenceInMinutes } from 'date-fns';

/**
 * Format currency (PHP)
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format time (relative or absolute)
 */
export function formatTime(timestamp: string): string {
  try {
    const date = parseISO(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return formatDistanceToNow(date, { addSuffix: true });
    } else {
      return format(date, 'MMM d, yyyy h:mm a');
    }
  } catch {
    return timestamp;
  }
}

/**
 * Format absolute time
 */
export function formatAbsoluteTime(timestamp: string): string {
  try {
    const date = parseISO(timestamp);
    return format(date, 'h:mm a');
  } catch {
    return timestamp;
  }
}

/**
 * Calculate SLA time remaining
 * @param createdAt - Order creation timestamp
 * @param slaMinutes - SLA duration in minutes
 * @returns Minutes remaining (negative if overdue)
 */
export function calculateSLARemaining(createdAt: string, slaMinutes: number): number {
  try {
    const created = parseISO(createdAt);
    const now = new Date();
    const elapsed = differenceInMinutes(now, created);
    return slaMinutes - elapsed;
  } catch {
    return 0;
  }
}

/**
 * Format SLA remaining time
 */
export function formatSLATime(minutes: number): string {
  if (minutes < 0) {
    return `Overdue by ${Math.abs(minutes)} min`;
  }
  if (minutes < 60) {
    return `${minutes} min left`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m left`;
}

/**
 * Format duration (minutes to readable)
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

/**
 * Format percentage
 */
export function formatPercentage(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format order ID (show last 6 characters)
 */
export function formatOrderId(orderId: string): string {
  if (orderId.length <= 8) return orderId;
  return `#${orderId.substring(orderId.length - 6).toUpperCase()}`;
}

/**
 * Mask customer phone number (show last 4 digits)
 */
export function maskPhone(phone: string): string {
  if (phone.length < 4) return phone;
  return `**** ${phone.substring(phone.length - 4)}`;
}

/**
 * Format number with commas
 */
export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}


















